"use client";

/**
 * Search sidebar for option images, characters' backdrops and watermarks.
 *
 * Two sources: brand logos (Simple Icons) and photos (Openverse). Logos are the
 * default because a "X vs Y" comparison is almost always two brand marks, and
 * no stock-photo library has those.
 *
 * Picking is awaited here rather than fire-and-forget: the chosen tile shows a
 * spinner and the sheet stays open until the bytes are in. A pick that closed
 * the sheet immediately and downloaded in the background was indistinguishable
 * from a pick that failed — nothing moved either way, and the failure surfaced
 * in a status panel on the other side of the page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, ImageOff, Loader2, Search } from "lucide-react";
import { searchImages, type ImageSearchResult } from "@/comparison/image-picker";
import { resolveTap, type TapGesture } from "@/comparison/tap-gesture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/utils/ui";

type Source = "logos" | "photos";

const SOURCE_LABELS: Record<Source, string> = {
	logos: "Brand logos",
	photos: "Photos",
};

export function ImageSearchSheet({
	open,
	onOpenChange,
	title,
	initialQuery,
	onPick,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	initialQuery?: string;
	/** Resolves once the image is in the form; rejecting keeps the sheet open. */
	onPick: (result: ImageSearchResult) => Promise<void>;
}) {
	const [query, setQuery] = useState(initialQuery ?? "");
	const [source, setSource] = useState<Source>("logos");
	const [results, setResults] = useState<ImageSearchResult[]>([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(false);
	/** The search is debounced, so "nothing found" must wait for a real answer. */
	const [loaded, setLoaded] = useState(false);
	/** These are suggestions, not something the user asked for — say so. */
	const [featured, setFeatured] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [pendingId, setPendingId] = useState<string | null>(null);
	const [pickError, setPickError] = useState<string | null>(null);
	// Provider CDNs occasionally serve a dead URL. Those tiles are greyed out and
	// made unpickable, but they keep their slot: a thumbnail fails over the
	// network *seconds* after the grid has rendered, so removing it pulls every
	// later tile up a place while the user is already reaching for one — and the
	// pick lands on whatever slid into the gap. Measured at 2 of 20 on one photo
	// search, which is roughly how often a pick came back with the wrong picture.
	const [broken, setBroken] = useState<Set<string>>(new Set());
	const requestRef = useRef<AbortController | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const pickingRef = useRef(false);
	/** The tile the finger went down on — see `resolveTap` for why that, not the
	    tile the tap came up over. */
	const gestureRef = useRef<TapGesture<ImageSearchResult> | null>(null);

	const run = useCallback(
		async ({ nextPage }: { nextPage: number }) => {
			const trimmed = query.trim();
			requestRef.current?.abort();
			const controller = new AbortController();
			requestRef.current = controller;

			setLoading(true);
			setError(null);
			try {
				const data = await searchImages({
					query: trimmed,
					source,
					page: nextPage,
					signal: controller.signal,
				});
				setResults((previous) =>
					nextPage === 1 ? data.results : [...previous, ...data.results],
				);
				setHasMore(data.hasMore);
				setPage(nextPage);
				setFeatured(data.featured);
				setLoaded(true);
			} catch (caught) {
				if (controller.signal.aborted) return;
				setError(caught instanceof Error ? caught.message : "Search failed.");
			} finally {
				if (!controller.signal.aborted) setLoading(false);
			}
		},
		[query, source],
	);

	// Debounced search on query/source change, so typing doesn't hammer the API.
	useEffect(() => {
		if (!open) return;
		const timer = setTimeout(() => void run({ nextPage: 1 }), 350);
		return () => clearTimeout(timer);
	}, [open, run]);

	useEffect(() => {
		if (!open) return;
		setQuery(initialQuery ?? "");
		setPickError(null);
		setPendingId(null);
		setLoaded(false);
		gestureRef.current = null;
	}, [open, initialQuery]);

	// Focus the field on a mouse, never on a touch. A phone keyboard is what
	// makes picking fragile — dismissing it resizes the sheet mid-tap — and the
	// sheet usually opens with the label already in the field, so there is
	// nothing to type anyway. (The sheet itself takes no opening focus: see
	// `onOpenAutoFocus` in `ui/sheet`.)
	useEffect(() => {
		if (!open) return;
		if (!window.matchMedia("(pointer: fine)").matches) return;
		inputRef.current?.focus();
	}, [open]);

	useEffect(() => () => requestRef.current?.abort(), []);

	// `pickingRef`, not `pendingId`, is the guard: pointerup and the click that
	// follows it land in the same tick, before any state update has been applied.
	const choose = useCallback(
		async ({ result }: { result: ImageSearchResult }) => {
			if (pickingRef.current) return;
			pickingRef.current = true;
			setPendingId(result.id);
			setPickError(null);
			try {
				await onPick(result);
			} catch (caught) {
				setPickError(
					caught instanceof Error
						? caught.message
						: "Couldn't use that image. Try another, or upload one.",
				);
			} finally {
				pickingRef.current = false;
				setPendingId(null);
			}
		},
		[onPick],
	);

	/**
	 * Resolves the pending touch, whichever event got here first. Returns the
	 * action taken so a tile can tell "this event was mine to handle" from "a
	 * touch already owns it".
	 */
	const commit = useCallback(
		({ x, y }: { x: number; y: number }) => {
			const gesture = gestureRef.current;
			const outcome = resolveTap({ gesture, x, y, now: Date.now() });

			if (outcome.action === "discard") gestureRef.current = null;
			if (outcome.action === "pick" && gesture) {
				// Marked rather than cleared: a cleared gesture is indistinguishable
				// from a mouse click, which the tile would then pick a second time.
				gesture.done = true;
				void choose({ result: outcome.target });
			}

			return outcome.action;
		},
		[choose],
	);

	/** Only for the empty state — the grid itself always renders every result. */
	const usable = results.filter((result) => !broken.has(result.id));

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			{/* `p-0` so the header and the search controls can be one pinned block
			    with the results scrolling under them; the sheet's own padding is
			    re-applied per band below. */}
			<SheetContent
				side="right"
				className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
			>
				{/* `text-left` overrides the shared header's mobile `text-center`,
				    which ran the title underneath the close button; `pr-12` keeps it
				    clear of that button at every width. */}
				<div className="border-border flex flex-col gap-3 border-b px-4 pt-4 pb-3 sm:px-5">
					<SheetHeader className="pr-12 text-left">
						<SheetTitle className="text-base leading-tight">{title}</SheetTitle>
						<SheetDescription className="text-xs">
							Free for commercial use. Logos come from Simple Icons, photos from
							Openverse.
						</SheetDescription>
					</SheetHeader>

					<div className="relative">
						<Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
						<Input
							ref={inputRef}
							enterKeyHint="search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={
								source === "logos"
									? "instagram, tiktok, python…"
									: "mountains, office…"
							}
							className="pl-9"
						/>
					</div>

					<div className="bg-muted flex rounded-md p-1">
						{(["logos", "photos"] as Source[]).map((option) => (
							<button
								key={option}
								type="button"
								onClick={() => setSource(option)}
								className={cn(
									"flex-1 cursor-pointer rounded-sm px-3 py-1.5 text-sm font-medium transition-colors",
									source === option
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								{SOURCE_LABELS[option]}
							</button>
						))}
					</div>
				</div>

				{/* Closing the keyboard the moment a finger enters the results means the
				    reflow it causes happens before the tap, not during it. */}
				<div
					className="flex-1 overflow-y-auto px-4 py-4 sm:px-5"
					onTouchStart={() => inputRef.current?.blur()}
					// The last resort for a reflowed tap: a click that missed every tile
					// and landed on the padding or the gap between them still resolves
					// the touch, because the touch remembers its own tile.
					onClick={(event) => commit({ x: event.clientX, y: event.clientY })}
				>
					{pickError ? (
						<div className="border-destructive/40 bg-destructive/5 mb-3 flex items-start gap-2 rounded-lg border p-3">
							<AlertCircle className="text-destructive mt-0.5 size-4 shrink-0" />
							<p className="text-destructive text-xs">{pickError}</p>
						</div>
					) : null}

					{error ? (
						<div className="py-6 text-center">
							<p className="text-muted-foreground text-sm">{error}</p>
							{source === "photos" ? (
								<p className="text-muted-foreground mt-1 text-xs">
									Openverse is flaky sometimes. Try the Brand logos tab, or
									upload an image instead.
								</p>
							) : null}
						</div>
					) : null}

					{!error && loaded && usable.length === 0 && !loading ? (
						<p className="text-muted-foreground py-10 text-center text-sm">
							{results.length > 0
								? "None of those images would load. Try another word."
								: "Nothing found. Try another word."}
						</p>
					) : null}

					{/* The grid opens on suggestions rather than on nothing, so it has to
					    say that these were not asked for. */}
					{featured && usable.length > 0 ? (
						<p className="text-muted-foreground mb-2 text-xs">
							{source === "logos"
								? "Popular marks — or search for any brand."
								: "A few to start with — or search for anything."}
						</p>
					) : null}

					<div
						className={cn(
							"grid grid-cols-3 gap-2 sm:grid-cols-4",
							usable.length === 0 ? "hidden" : "",
						)}
					>
						{results.map((result) => {
							const dead = broken.has(result.id);
							return (
								<button
									key={result.id}
									type="button"
									title={
										dead
											? `${result.title} — this one didn't load`
											: result.title
									}
									disabled={pendingId !== null || dead}
									onPointerDown={(event) => {
										if (event.pointerType === "mouse") return;
										gestureRef.current = {
											target: result,
											x: event.clientX,
											y: event.clientY,
											at: Date.now(),
											done: false,
										};
									}}
									onPointerUp={(event) => {
										if (event.pointerType === "mouse") return;
										commit({ x: event.clientX, y: event.clientY });
									}}
									onClick={(event) => {
										// Only a click with no touch behind it means *this* tile: a
										// touch-derived one is reported against whatever tile has
										// slid under the finger, so the gesture decides instead.
										const action = commit({
											x: event.clientX,
											y: event.clientY,
										});
										if (action === "ignore") void choose({ result });
									}}
									className={cn(
										"border-border bg-background relative flex aspect-square items-center justify-center rounded-md border p-2 transition-colors",
										dead
											? "border-dashed opacity-45"
											: "hover:border-primary cursor-pointer",
										pendingId && pendingId !== result.id
											? "cursor-default opacity-40"
											: "",
									)}
								>
									{dead ? (
										<ImageOff className="text-muted-foreground size-4" />
									) : (
										/* eslint-disable-next-line @next/next/no-img-element -- remote thumbnail, no CORS needed to display */
										<img
											src={result.thumbUrl}
											alt={result.title}
											loading="lazy"
											referrerPolicy="no-referrer"
											onError={(event) => {
												// Openverse's /thumb/ endpoint fails often; fall back to
												// the provider's own URL once before giving up on a tile.
												const element = event.currentTarget;
												if (
													element.dataset.fallback !== "1" &&
													result.downloadUrl !== result.thumbUrl
												) {
													element.dataset.fallback = "1";
													element.src = result.downloadUrl;
													return;
												}
												setBroken((previous) =>
													new Set(previous).add(result.id),
												);
											}}
											className="max-h-full max-w-full object-contain"
										/>
									)}
									{pendingId === result.id ? (
										<span className="bg-background/70 absolute inset-0 flex items-center justify-center rounded-md">
											<Loader2 className="text-primary size-5 animate-spin" />
										</span>
									) : null}
								</button>
							);
						})}
					</div>

					{loading ? (
						<div className="text-muted-foreground flex justify-center py-6">
							<Loader2 className="size-5 animate-spin" />
						</div>
					) : null}

					{hasMore && !loading ? (
						<div className="flex justify-center py-4">
							<Button
								variant="outline"
								size="sm"
								disabled={pendingId !== null}
								onClick={() => void run({ nextPage: page + 1 })}
							>
								Load more
							</Button>
						</div>
					) : null}
				</div>
			</SheetContent>
		</Sheet>
	);
}
